import { useState, useCallback } from 'react';
import { Strings } from '@/constants/strings';
import {
  calculateRisk,
  validateSystolicBp,
  validateTotalCholesterol,
  validateHdlCholesterol,
  validateNonHdl,
  hasBlockingError,
  hasWarning,
  type CalculatorMode,
  type Gender,
  type CalculationResult,
} from '@/data';
import score2Harita from '@/assets/score2-harita.png';
import { MedicalInput } from './MedicalInput';
import { GenderSelection } from './GenderSelection';
import { InfoModal } from './InfoModal';
import { AgePicker } from './AgePicker';
import { ToggleRow } from './ToggleRow';

export type UserMode = 'healthcare' | 'public';

type InfoKey = 'tobacco' | 'diabetes' | 'sbp' | 'totalChol' | 'hdl' | null;

const INFO_CONTENT: Record<Exclude<InfoKey, null>, { title: string; content: string }> = {
  tobacco: { title: Strings.tobacco, content: Strings.infoTobacco },
  diabetes: { title: Strings.diabetes, content: Strings.infoDiabetes },
  sbp: { title: Strings.systolicBp, content: Strings.infoSystolicBp },
  totalChol: { title: Strings.totalCholesterol, content: Strings.infoTotalCholesterol },
  hdl: { title: Strings.hdlCholesterol, content: Strings.infoHdlCholesterol },
};

interface RiskCalculatorProps {
  userMode: UserMode;
  onResult: (result: CalculationResult) => void;
}

export function RiskCalculator({ userMode, onResult }: RiskCalculatorProps) {
  const [showModeBInstruction, setShowModeBInstruction] = useState(userMode === 'public');
  const [mode, setMode] = useState<CalculatorMode>('SCORE2');
  const [gender, setGender] = useState<Gender | null>(null);
  const [age, setAge] = useState(mode === 'SCORE2' ? 55 : 75);
  const [smoker, setSmoker] = useState<boolean | null>(null);
  const [diabetes, setDiabetes] = useState<boolean | null>(null);
  const [sbp, setSbp] = useState('');
  const [totalChol, setTotalChol] = useState('');
  const [hdl, setHdl] = useState('');
  const [infoModal, setInfoModal] = useState<InfoKey>(null);

  const showInfo = userMode === 'public';

  const sbpNum = parseInt(sbp, 10) || 0;
  const totalCholNum = parseInt(totalChol, 10) || 0;
  const hdlNum = parseInt(hdl, 10) || 0;

  const vbSbp = validateSystolicBp(sbpNum);
  const vbTotal = validateTotalCholesterol(totalCholNum);
  const vbHdl = validateHdlCholesterol(hdlNum);
  const vbNonHdl = validateNonHdl(totalCholNum, hdlNum);

  const blocking = hasBlockingError(vbSbp, vbTotal, vbHdl, vbNonHdl);
  const hasWarn = hasWarning(vbSbp, vbTotal, vbHdl);

  const minAge = mode === 'SCORE2' ? 40 : 70;
  const maxAge = mode === 'SCORE2' ? 69 : 110;

  const canCalculate =
    gender !== null &&
    smoker !== null &&
    (mode === 'SCORE2' || diabetes !== null) &&
    sbpNum >= 80 &&
    sbpNum <= 300 &&
    totalCholNum >= 50 &&
    totalCholNum <= 1000 &&
    hdlNum >= 10 &&
    hdlNum <= 120 &&
    totalCholNum - hdlNum >= 0 &&
    !blocking;

  const handleCalculate = useCallback(() => {
    if (!canCalculate || gender === null || smoker === null) return;
    if (mode === 'SCORE2-OP' && diabetes === null) return;

    const result = calculateRisk({
      mode,
      gender,
      age,
      smoker,
      systolicBp: sbpNum,
      totalCholesterol: totalCholNum,
      hdlCholesterol: hdlNum,
      diabetes: mode === 'SCORE2-OP' ? diabetes ?? undefined : undefined,
    });

    if (result) onResult(result);
  }, [canCalculate, mode, gender, age, smoker, diabetes, sbpNum, totalCholNum, hdlNum, onResult]);

  const activeInfo = infoModal ? INFO_CONTENT[infoModal] : null;

  return (
    <div className="min-h-screen-safe flex flex-1 flex-col bg-surface pb-keyboard">
      <div className="flex-1 overflow-y-auto px-6 pb-8 pt-6">
        <img
          src={score2Harita}
          alt="SCORE2 coğrafi risk bölgeleri haritası"
          className="mb-4 w-[65%] object-contain"
          style={{ height: 'auto' }}
        />
        <p className="mb-6 text-sm italic text-text-muted">
          Türkiye, SCORE2 kılavuzuna göre Yüksek Risk bölgesinde yer almaktadır. Hesaplamalar bu bölge verilerine göre optimize edilmiştir.
        </p>

        {showModeBInstruction && (
          <div className="mb-5 rounded-lg border-l-4 border-warning bg-warning-bg p-3">
            <p className="mb-2 text-sm text-text-heading">{Strings.modeBInstruction}</p>
            <button
              type="button"
              onClick={() => setShowModeBInstruction(false)}
              className="text-sm font-bold text-primary"
            >
              Tamam
            </button>
          </div>
        )}

        <div className="mb-6 flex rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => {
              setMode('SCORE2');
              setAge(55);
              setDiabetes(null);
            }}
            className={`flex-1 rounded-lg py-3 text-center text-sm font-bold transition
              ${mode === 'SCORE2' ? 'bg-primary text-white hover:bg-primary-active active:bg-primary-active' : 'text-text-muted'}
            `}
          >
            {Strings.score2Tab}
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('SCORE2-OP');
              setAge(75);
            }}
            className={`flex-1 rounded-lg py-3 text-center text-sm font-bold transition
              ${mode === 'SCORE2-OP' ? 'bg-primary text-white hover:bg-primary-active active:bg-primary-active' : 'text-text-muted'}
            `}
          >
            {Strings.score2OpTab}
          </button>
        </div>

        <div className="mb-5">
          <GenderSelection
            value={gender}
            onValueChange={(v) => setGender(v)}
          />
        </div>

        <p className="mb-2 text-[15px] font-medium text-text-heading">{Strings.age}</p>
        <AgePicker minAge={minAge} maxAge={maxAge} value={age} onValueChange={setAge} />

        <ToggleRow
          label={Strings.tobacco}
          value={smoker}
          onValueChange={setSmoker}
          showInfoIcon={showInfo}
          onInfoPress={() => setInfoModal('tobacco')}
        />

        {mode === 'SCORE2-OP' && (
          <ToggleRow
            label={Strings.diabetes}
            value={diabetes}
            onValueChange={setDiabetes}
            showInfoIcon={showInfo}
            onInfoPress={() => setInfoModal('diabetes')}
          />
        )}

        <MedicalInput
          label={Strings.systolicBp}
          value={sbp}
          onChange={setSbp}
          placeholder="örn. 120"
          validationStatus={vbSbp.status}
          validationMessage={vbSbp.message}
          showInfoIcon={showInfo}
          onInfoPress={() => setInfoModal('sbp')}
        />

        <MedicalInput
          label={Strings.totalCholesterol}
          value={totalChol}
          onChange={setTotalChol}
          placeholder="örn. 200"
          validationStatus={vbTotal.status}
          validationMessage={vbTotal.message}
          showInfoIcon={showInfo}
          onInfoPress={() => setInfoModal('totalChol')}
        />

        <MedicalInput
          label={Strings.hdlCholesterol}
          value={hdl}
          onChange={setHdl}
          placeholder="örn. 50"
          validationStatus={vbHdl.status}
          validationMessage={vbHdl.message}
          showInfoIcon={showInfo}
          onInfoPress={() => setInfoModal('hdl')}
        />

        {vbNonHdl.message && (
          <p className="-mt-2 mb-3 text-xs text-error">{vbNonHdl.message}</p>
        )}

        {hasWarn && !blocking && (
          <div className="mb-4 rounded-lg bg-warning-bg p-3">
            <p className="text-[13px] text-text-heading">
              {Strings.calculatorWarningRange}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={handleCalculate}
          disabled={!canCalculate}
          className={`w-full rounded-2xl py-4 text-lg font-bold transition disabled:opacity-60
            ${canCalculate ? 'bg-primary text-white hover:bg-primary-active active:bg-primary-active' : 'bg-slate-400 text-white'}
          `}
        >
          {Strings.calculate}
        </button>

        <div className="h-10" />
      </div>

      {activeInfo && (
        <InfoModal
          visible={!!infoModal}
          title={activeInfo.title}
          content={activeInfo.content}
          onClose={() => setInfoModal(null)}
        />
      )}
    </div>
  );
}
